// src/components/ecommerce/ProductCard.jsx
import React, { useState } from 'react';
import { 
  ShoppingCart,
  Heart,
  Eye,
  Star,
  Package,
  Tag,
  ExternalLink,
  Share2,
  MessageCircle,
  Zap,
  Truck,
  Shield,
  Clock,
  MapPin,
  AlertCircle,
  Check,
  Loader2,
  Plus,
  Minus
} from 'lucide-react';
import { getStoreTypeConfig } from './utils/storeTypeConfig';

export const ProductCard = ({ 
  product, 
  bookData,
  storeView = 'grid',
  onClick, 
  onAddToCart,
  isInCart = false,
  cartItem = null,
  loading = false,
  styling = {},
  className = '' 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isLiked, setIsLiked] = useState(false);

  // Extract design settings with defaults
  const design = bookData.design;
  const storeSettings = bookData.storeSettings;
  const primaryColor = design.primaryColor || '#3b82f6';
  const accentColor = design.accentColor || '#f59e0b';
  const cardRadius = design.cardRadius || '12px';
  const shadowLevel = design.shadowLevel || 'medium';
  const imageFit = design.imageFit || 'cover';
  const imageAspectRatio = design.imageAspectRatio || '1:1';
  const fontFamily = design.fontFamily || 'Inter';
  const gridColumns = design.gridColumns || 3; // For ProductGrid
  const config = getStoreTypeConfig(storeSettings?.storeType || 'general');
  
  // Get shadow class based on shadow level
  const getShadowClass = () => {
    switch (shadowLevel) {
      case 'none': return '';
      case 'light': return 'shadow-sm';
      case 'medium': return 'shadow-md';
      case 'strong': return 'shadow-lg';
      case 'extra': return 'shadow-xl';
      default: return 'shadow-md';
    }
  };

  // Get aspect ratio class
  const getAspectRatioClass = () => {
    switch (imageAspectRatio) {
      case '4:3': return 'aspect-[4/3]';
      case '3:4': return 'aspect-[3/4]';
      case '16:9': return 'aspect-video';
      default: return 'aspect-square';
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAddToCart?.(product, quantity, selectedVariant);
  };

  const handleQuickView = (e) => {
    e.stopPropagation();
    onClick?.(product);
  };

  const handleLike = (e) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    // Add wishlist logic here
  };

  const isOutOfStock = product.quantity === 0 || product.status === 'out-of-stock';
  const isOnSale = product.salePrice && product.salePrice < product.price;
  const discount = isOnSale ? Math.round(((product.price - product.salePrice) / product.price) * 100) : 0;

  if (storeView === 'masonry') {
    className += ' break-inside-avoid mb-4';
  }

  return (
    <div 
      className={`bg-white rounded-lg overflow-hidden transition-all duration-200 cursor-pointer group hover:-translate-y-1 ${getShadowClass()} hover:shadow-lg ${className}`}
      style={{ 
        borderRadius: cardRadius,
        fontFamily: fontFamily
      }}
      onClick={handleQuickView}
    >
      {/* Image Container */}
      <div className={`relative ${getAspectRatioClass()} overflow-hidden bg-gray-100`}>
        {/* Product Image */}
        {product.images && product.images.length > 0 && !imageError ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className={`w-full h-full transition-transform duration-300 group-hover:scale-105`}
            style={{ objectFit: imageFit }}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <Package className="h-12 w-12 text-gray-400" />
          </div>
        )}

        {/* Loading Overlay */}
        {imageLoading && !imageError && (
          <div className="absolute inset-0 bg-gray-100 animate-pulse" />
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {isOnSale && (
            <span 
              className="text-xs font-semibold px-2 py-1 rounded-full text-white"
              style={{ backgroundColor: accentColor }}
            >
              -{discount}%
            </span>
          )}
          {product.featured && (
            <span 
              className="text-xs font-semibold px-2 py-1 rounded-full"
              style={{ 
                backgroundColor: `${accentColor}20`,
                color: accentColor 
              }}
            >
              Featured
            </span>
          )}
          {product.digital && (
            <span 
              className="text-xs font-semibold px-2 py-1 rounded-full"
              style={{ 
                backgroundColor: `${primaryColor}20`,
                color: primaryColor 
              }}
            >
              <Zap className="inline h-3 w-3 mr-1" />
              Digital
            </span>
          )}
          {isOutOfStock && (
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-600">
              Out of Stock
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleLike}
            className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
              isLiked 
                ? 'bg-red-100 text-red-600' 
                : 'bg-white/80 hover:bg-white text-gray-600 hover:text-red-600'
            }`}
          >
            <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
          </button>
          <button
            onClick={handleQuickView}
            className="p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Eye className="h-4 w-4" />
          </button>
        </div>

       {!config.cartEnabled ? (
          // For services - show contact/booking buttons
          <div className="space-y-2">
            {config.showBookingButton && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle booking action
                  onClick?.(product);
                }}
                className="w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-white"
                style={{ backgroundColor: styling.primaryColor }}
              >
                <Clock className="h-4 w-4" />
                Book Service
              </button>
            )}
            {config.showContactButton && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Handle contact action  
                }}
                className="w-full py-2 px-4 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 flex items-center justify-center gap-2"
              >
                <MessageCircle className="h-4 w-4" />
                Inquire
              </button>
            )}
          </div>
        ) : (
          // Original Add to Cart button for products
          <button
            onClick={handleAddToCart}
            disabled={isOutOfStock || loading}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              isOutOfStock
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : isInCart
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'text-white hover:opacity-90'
            }`}
            style={{ 
              backgroundColor: !isOutOfStock && !isInCart ? styling.primaryColor : undefined
            }}
          >

              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isInCart ? (
                <>
                  <Check className="h-4 w-4" />
                  In Cart
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4" />
                  Add to Cart
                </>
              )}
            </button>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4">
        {/* Category & Brand */}
        {(product.category || product.brand) && (
          <div className="flex items-center gap-2 mb-2">
            
            {product.category && product.brand && (
              <span className="text-xs text-gray-300">•</span>
            )}
            {product.brand && (
              <span className="text-xs font-medium" style={{ color: primaryColor }}>
                {product.brand}
              </span>
            )}
          </div>
        )}

        {/* Product Name */}
        <h3 className="font-semibold text-sm text-gray-900 mb-2 line-clamp-2 group-hover:text-gray-700 transition-colors">
          {product.name}
        </h3>

        {/* Rating 
        {product.rating && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3 w-3 ${
                    i < Math.floor(product.rating) 
                      ? 'fill-current' 
                      : ''
                  }`}
                  style={{ color: accentColor }}
                />
              ))}
            </div>
            {/*
            <span className="text-xs text-gray-500">
              ({product.reviewCount || 0})
            </span>
          </div>
        )}
        */}
        {/* Price */}
        <div className="flex items-center gap-2">
          <span 
            className="text-lg text-red-600 font-bold"
           
          >
            R{isOnSale ? product.salePrice : product.price}
          </span>
          {isOnSale && (
            <span className="text-sm text-gray-500 line-through">
              R{product.price?.toFixed(2)}
            </span>
          )}
        </div>

        {/* Variants Preview */}
        {product.variants && product.variants.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-1">
              {product.variants.slice(0, 3).map((variant, index) => (
                <button
                  key={variant.id || index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVariant(variant);
                  }}
                  className={`text-xs px-2 py-1 rounded border transition-colors ${
                    selectedVariant?.id === variant.id
                      ? 'border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {variant.name}
                </button>
              ))}
              {product.variants.length > 3 && (
                <span className="text-xs text-gray-500 px-2 py-1">
                  +{product.variants.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Quantity in Cart */}
        {isInCart && cartItem && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-gray-600">In cart:</span>
            <div className="flex items-center border border-gray-300 rounded">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (cartItem.quantity > 1) {
                    // Update cart quantity logic
                  }
                }}
                className="p-1 hover:bg-gray-100 transition-colors"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="px-2 text-sm">{cartItem.quantity}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Update cart quantity logic
                }}
                className="p-1 hover:bg-gray-100 transition-colors"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>
        {/* Add to Cart Button (fallback if no hover) */}
        <button
          onClick={handleAddToCart}
          disabled={isOutOfStock || loading}
          className={`w-full mb-4 py-2 px-0 rounded-sm font-medium transition-colors flex items-center justify-center gap-2 ${
            isOutOfStock
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : isInCart
              ? 'bg-green-100 text-green-700 border border-green-300'
              : 'text-white hover:opacity-90'
          } md:hidden`} // Hide on desktop (shown on hover), visible on mobile
          style={{ 
            backgroundColor: !isOutOfStock && !isInCart ? primaryColor : undefined,
            borderRadius: parseInt(cardRadius) > 12 ? '0px' : '0px'
          }}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isOutOfStock ? (
            <>
              <AlertCircle className="h-4 w-4" />
              Out of Stock
            </>
          ) : isInCart ? (
            <>
              <Check className="h-4 w-4" />
              In Cart
            </>
          ) : (
            <>
              <ShoppingCart className="h-4 w-4" />
              Add to Cart
            </>
          )}
        </button>
      </div>
  );
};